/**
 * 費用・支払・請求（G3）画面の共通部品。
 *  - OperatorSelect：事業実施者（G1 の BusinessOperatorSelect。漁協は自組織固定）
 *  - VesselExcludePicker：「船名 ※ 出力対象外条件」の複数選択（船名一括選択・一括クリア）
 *  - Money / Num：金額・数値の表示（等幅・右寄せ）
 */
import { useId, type ReactNode } from 'react';
import { CheckSquare, Square } from 'lucide-react';
import { Button, Checkbox } from '@/components';
import { BusinessOperatorSelect } from '@/features/masters/components/BusinessOperatorSelect';
import { formatNumber } from '@/lib/format';
import { cx } from '@/lib/cx';
import { useVesselOptions } from './api';
import './expenses.css';

/** 事業実施者の単一選択（G1 共通部品 BusinessOperatorSelect を使用。漁協は自組織固定） */
export function OperatorSelect({
  year,
  value,
  onChange,
  id,
  invalid,
  placeholder = '選択してください',
}: {
  year: number;
  value: number | null;
  onChange: (v: number | null) => void;
  id?: string;
  invalid?: boolean;
  placeholder?: string;
}) {
  return <BusinessOperatorSelect id={id} fiscalYear={year} value={value} onChange={onChange} invalid={invalid} placeholder={placeholder} />;
}

export function VesselExcludePicker({
  year,
  operatorId,
  value,
  onChange,
  label = '船名 ※ 出力対象外条件',
}: {
  year: number;
  operatorId: number | null;
  value: number[];
  onChange: (ids: number[]) => void;
  label?: string;
}) {
  const { data, isPending } = useVesselOptions(year, operatorId);
  const labelId = useId();
  const vessels = data ?? [];
  const toggle = (id: number) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div className="bs-exp-exclude" role="group" aria-labelledby={labelId}>
      <div className="bs-exp-exclude__head">
        <span id={labelId} className="bs-exp-exclude__label">
          {label}
        </span>
        <span className="bs-exp-exclude__count bs-num">{value.length ? `${value.length} 隻を除外` : '除外なし'}</span>
        <span className="bs-exp-exclude__ops">
          <Button size="sm" variant="ghost" icon={<CheckSquare size={14} />} disabled={!vessels.length} onClick={() => onChange(vessels.map((v) => v.id))}>
            船名一括選択
          </Button>
          <Button size="sm" variant="ghost" icon={<Square size={14} />} disabled={!value.length} onClick={() => onChange([])}>
            船名一括クリア
          </Button>
        </span>
      </div>
      <div className="bs-exp-exclude__list">
        {operatorId == null ? (
          <p className="bs-exp-muted">事業実施者を選択すると参加船が表示されます。</p>
        ) : isPending ? (
          <p className="bs-exp-muted">読み込み中…</p>
        ) : vessels.length === 0 ? (
          <p className="bs-exp-muted">参加船がありません。</p>
        ) : (
          vessels.map((v) => (
            <Checkbox
              key={v.id}
              label={
                <>
                  <span className="bs-num bs-exp-exclude__no">{v.displayOrder}</span>
                  {v.name}
                </>
              }
              checked={value.includes(v.id)}
              onChange={() => toggle(v.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

/** 金額（3 桁区切り・等幅）。null は空欄 */
export function Money({ value, unit, className }: { value: number | null | undefined; unit?: string; className?: string }) {
  if (value == null) return <span className={cx('bs-num', className)} />;
  return (
    <span className={cx('bs-num', className)}>
      {formatNumber(value)}
      {unit && <small className="bs-exp-unit">{unit}</small>}
    </span>
  );
}

/** 小数を含む数値（日数・数量） */
export function Num({ value, digits = 2 }: { value: number | null | undefined; digits?: number }) {
  if (value == null) return <span className="bs-num" />;
  const s = Number.isInteger(value) ? formatNumber(value) : formatNumber(value, digits).replace(/\.?0+$/, '');
  return <span className="bs-num">{s}</span>;
}

/** 合計値のサマリ（ラベル＋値の横並び） */
export function SummaryStrip({ items }: { items: { label: string; value: ReactNode; emphasis?: boolean }[] }) {
  return (
    <dl className="bs-exp-summary">
      {items.map((i) => (
        <div key={i.label} className={cx('bs-exp-summary__item', i.emphasis && 'is-emphasis')}>
          <dt>{i.label}</dt>
          <dd>{i.value}</dd>
        </div>
      ))}
    </dl>
  );
}
